/**
 * Загрузка счёта из 1С в заказ: схемы запросов и DTO.
 * Паспорт — docs/features/unf-invoice-upload.md.
 */
import { z } from 'zod'
import type { Role } from '../constants/roles'
import type { SellerEntity } from '../constants/orders'
import { MATRIX_LABELS } from '../constants/matrices'

/** Кто загружает счёт из 1С: продажные роли по своим заявкам, бухгалтер и администратор — по любым. */
export const UNF_INVOICE_UPLOAD_ROLES: Role[] = [
  'ADMIN',
  'MANAGER',
  'ROP',
  'ASSISTANT',
  'ACCOUNTANT',
]

const money = z.number().finite().min(0).max(1_000_000_000_000)

/** Счёт, разобранный на сайте из Excel (parseUnfInvoice). */
export const UnfInvoiceInputSchema = z.object({
  number: z.string().trim().min(1).max(40),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  sellerName: z.string().max(400),
  sellerInn: z.string().max(20),
  buyerName: z.string().max(400),
  buyerInn: z.string().max(20),
  lines: z
    .array(
      z.object({
        no: z.number().int().min(0).max(100_000),
        name: z.string().trim().min(1).max(500),
        manufacturer: z.string().max(200),
        quantity: z.number().finite().positive().max(10_000_000),
        unit: z.string().max(20),
        price: money,
        sum: money,
      }),
    )
    .min(1)
    .max(2000),
  total: money.nullable(),
  payable: money.nullable(),
  vat: z
    .array(z.object({ rate: z.number().min(0).max(100), amount: money, included: z.boolean() }))
    .max(10),
  vatOnTop: z.boolean(),
})
export type UnfInvoiceInput = z.infer<typeof UnfInvoiceInputSchema>

/**
 * Строка счёта, которой не нашлось в заявке, — «это та же позиция, что №…» (21.10.2026):
 * в заявке партномер записан иначе, менеджер связывает строку с позицией сам.
 */
const UnfInvoiceLinkSchema = z.object({
  no: z.number().int().min(0).max(100_000),
  itemId: z.string().uuid(),
})
export type UnfInvoiceLink = z.infer<typeof UnfInvoiceLinkSchema>

/**
 * Новая позиция из строки счёта (21.10.2026): менеджер сам вносит квоту — матрицу,
 * поставщика и закупочную цену, он уже знает их, раз вписал товар в УНФ.
 */
export const UnfInvoiceNewLineSchema = z.object({
  no: z.number().int().min(0).max(100_000),
  matrix: z.enum(MATRIX_LABELS),
  supplierName: z.string().trim().min(1, 'Укажите поставщика').max(255),
  costPrice: z.number().finite().positive('Укажите закупочную цену').max(1_000_000_000),
  costCurrency: z.enum(['RUB', 'USD', 'EUR', 'CNY']),
})
export type UnfInvoiceNewLine = z.infer<typeof UnfInvoiceNewLineSchema>

export const PreviewUnfInvoiceSchema = z.object({
  invoice: UnfInvoiceInputSchema,
  /** Загрузка из карточки заказа — заявку искать не нужно. */
  requestId: z.string().uuid().optional(),
  /** Строки, связанные менеджером с позициями заявки («та же, что №…»). */
  links: z.array(UnfInvoiceLinkSchema).max(2000).optional(),
})
export const ApplyUnfInvoiceSchema = z.object({
  invoice: UnfInvoiceInputSchema,
  requestId: z.string().uuid(),
  /** Загрузка из карточки заказа: заявку выбрал человек, другие заявки клиента не сверяются. */
  fromOrder: z.boolean().optional(),
  /**
   * «Будут ещё счета» (21.10.2026): позиции заказа, которых нет ни в одном счёте 1С, ждут
   * счёта. Не отмечено — заказ подстраивается под счёт, такие позиции уходят в «Отказ клиента».
   */
  moreInvoices: z.boolean().optional(),
  links: z.array(UnfInvoiceLinkSchema).max(2000).optional(),
  newLines: z.array(UnfInvoiceNewLineSchema).max(2000).optional(),
})
/** Отложить счёт, который не сошёлся на 100%, в «Счета на проверке». */
export const DeferUnfInvoiceSchema = z.object({
  invoice: UnfInvoiceInputSchema,
  requestId: z.string().uuid().optional(),
})

/** Как строка счёта соотносится с позицией заявки. */
export type UnfInvoiceLineKind = 'MATCH' | 'CHANGED' | 'NEW'

export interface UnfInvoiceLineDiffDto {
  no: number
  name: string
  quantity: number
  price: number
  sum: number
  kind: UnfInvoiceLineKind
  itemId: string | null
  partNumber: string | null
  /** Количество и цена позиции в заявке сейчас (цена — из «Счёт» или последнего КП). */
  itemQuantity: number | null
  itemPrice: number | null
  /** Строку с позицией связал менеджер («та же, что №…»). */
  linked?: boolean
  /**
   * Как строка узнана, если не по партномеру в названии (21.10.2026): `UNF_CARD` — по артикулу
   * карточки УНФ с таким названием; `POSITION` — догадка: единственная позиция заказа с тем же
   * количеством и ценой.
   */
  matchedBy?: 'UNF_CARD' | 'POSITION'
}

export interface UnfInvoiceCandidateDto {
  requestId: string
  requestNumber: string | null
  requestStatus: string
  managerName: string | null
  orderId: string | null
  orderStatus: string | null
  /** Сколько строк счёта нашлось в заявке и сколько из них совпало полностью. */
  matched: number
  exact: number
  linesTotal: number
  lines: UnfInvoiceLineDiffDto[]
  /**
   * Позиции заказа, которых нет ни в этом счёте 1С, ни в другом загруженном или оплаченном:
   * без отметки «будут ещё счета» уходят в «Отказ клиента» с причиной «нет в счёте».
   */
  missing: Array<{ itemId: string; partNumber: string; quantity: number; price: number | null }>
  /** У заказа уже стоит отметка «будут ещё счета». */
  moreInvoicesExpected: boolean
  /** Сколько позиций в заказе (со «Счётом» или «Заказом») — с ними сравнивается счёт. */
  orderPositions: number
  /** Позиции заказа, с которыми можно связать строку «нет в заявке» (не заняты строками счёта). */
  linkable: Array<{ itemId: string; position: number; partNumber: string; quantity: number }>
  /** Заказ уже оплачен или закрыт: изменятся только номер и дата счёта. */
  onlyNumber: boolean
  /** Этот счёт уже загружен в заказ — загрузка обновит его. */
  reupload: boolean
  /** Почему загрузить нельзя (null — можно). */
  blocked: string | null
  /** Счёт сошёлся с заявкой на 100%: все строки совпали по партномеру, количеству и цене. */
  full: boolean
}

export interface UnfInvoicePreviewDto {
  entityId: SellerEntity | null
  client: { id: string; name: string } | null
  candidates: UnfInvoiceCandidateDto[]
  warnings: string[]
  /** Счёт нельзя провести вовсе (НДС сверху, незнакомый поставщик, покупателя нет). */
  blocked: string | null
  /**
   * FULL — счёт сошёлся на 100% ровно с одной заявкой (fullRequestId), его можно записать.
   * CHECK — нужна проверка: программа ничего не меняет, счёт можно отложить (reviewReason — почему).
   */
  verdict: 'FULL' | 'CHECK'
  fullRequestId: string | null
  reviewReason: string | null
}

/** Счёт из 1С, отложенный на проверку («Заказы» → «Счета на проверке»). */
export interface UnfInvoiceReviewDto {
  id: string
  invoiceNumber: string
  invoiceDate: string | null
  buyerName: string
  buyerInn: string
  payable: number | null
  reason: string
  requestId: string | null
  requestNumber: string | null
  createdByName: string | null
  createdAt: string
  /** Счёт целиком — для повторной сверки без загрузки файла. */
  invoice: UnfInvoiceInput
}

export interface UnfInvoiceApplyResultDto {
  orderId: string
  requestId: string
  orderCreated: boolean
  onlyNumber: boolean
  /** Позиций: совпало / изменены цена или количество / добавлено / «Отказ клиента» / ждут счёта. */
  matched: number
  changed: number
  added: number
  removed: number
  waiting: number
  /** Сколько поступлений из очереди банка разнеслось после загрузки. */
  autoAllocated: number
}
